import { useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';

const PROD_HOSTS = new Set(['add-individual-solutions.com', 'www.add-individual-solutions.com']);
const SUPABASE_URL = 'https://czrjcndncumiltdjrdbu.supabase.co';
const SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImN6cmpjbmRuY3VtaWx0ZGpyZGJ1Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzE4NTU2MDksImV4cCI6MjA4NzQzMTYwOX0.bJM7B1dBvOztc0gRMSYBXKN9viMRurWeu5ffIk7WmbE';

// ── Geo cache: fetch once per session ──
let geoCache: { country: string | null; city: string | null } | null = null;

function parseBrowser(ua: string): string {
  if (ua.includes('Firefox')) return 'Firefox';
  if (ua.includes('Edg')) return 'Edge';
  if (ua.includes('Chrome')) return 'Chrome';
  if (ua.includes('Safari')) return 'Safari';
  if (ua.includes('Opera') || ua.includes('OPR')) return 'Opera';
  return 'Other';
}

function parseDevice(ua: string): string {
  if (/Mobi|Android/i.test(ua)) return 'Mobile';
  if (/Tablet|iPad/i.test(ua)) return 'Tablet';
  return 'Desktop';
}

function patchDuration(rowId: string, durationSec: number) {
  const url = `${SUPABASE_URL}/rest/v1/site_analytics?id=eq.${rowId}`;
  try {
    fetch(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
        'Prefer': 'return=minimal',
      },
      body: JSON.stringify({ visit_duration_seconds: durationSec }),
      keepalive: true,
    }).catch(() => {});
  } catch (_e) {
    // ignore
  }
}

function getSessionId(): string {
  let sid = sessionStorage.getItem('_addis_sid');
  if (!sid) {
    sid = crypto.randomUUID();
    sessionStorage.setItem('_addis_sid', sid);
  }
  return sid;
}

async function fetchGeo(): Promise<{ country: string | null; city: string | null }> {
  if (geoCache) return geoCache;
  try {
    const { data } = await supabase.functions.invoke('geo-lookup');
    geoCache = { country: data?.country || null, city: data?.city || null };
  } catch (_e) {
    geoCache = { country: null, city: null };
  }
  return geoCache;
}

/**
 * Route-aware analytics tracker.
 * Call from a component inside <BrowserRouter> and pass location.pathname.
 */
export const useAnalyticsTracker = (pathname: string) => {
  const currentRowId = useRef<string | null>(null);
  const pageStartTime = useRef<number>(Date.now());

  // ── Track each route change as a new pageview ──
  useEffect(() => {
    if (!PROD_HOSTS.has(window.location.hostname)) return;
    // Skip admin routes from tracking
    if (pathname.startsWith('/admin')) return;

    pageStartTime.current = Date.now();

    const timer = setTimeout(async () => {
      const sessionId = getSessionId();
      const geo = await fetchGeo();
      const ua = navigator.userAgent;

      const result = await supabase.from('site_analytics').insert({
        page_path: pathname,
        referrer: document.referrer || null,
        user_agent: ua,
        session_id: sessionId,
        browser: parseBrowser(ua),
        device: parseDevice(ua),
        country: geo.country,
        city: geo.city,
      }).select('id').single();

      if (result.data?.id) {
        currentRowId.current = result.data.id;
        sessionStorage.setItem('_addis_row_id', result.data.id);
      }
    }, 200);

    return () => clearTimeout(timer);
  }, [pathname]);

  // ── Duration heartbeat (30s) + visibilitychange + beforeunload ──
  useEffect(() => {
    if (!PROD_HOSTS.has(window.location.hostname)) return;

    const updateDuration = () => {
      const rowId = currentRowId.current || sessionStorage.getItem('_addis_row_id');
      if (!rowId) return;
      const dur = Math.round((Date.now() - pageStartTime.current) / 1000);
      if (dur > 0) patchDuration(rowId, dur);
    };

    const heartbeat = setInterval(updateDuration, 30_000);
    const onHidden = () => { if (document.visibilityState === 'hidden') updateDuration(); };
    document.addEventListener('visibilitychange', onHidden);
    window.addEventListener('beforeunload', updateDuration);

    return () => {
      clearInterval(heartbeat);
      document.removeEventListener('visibilitychange', onHidden);
      window.removeEventListener('beforeunload', updateDuration);
    };
  }, []);

  // ── Section visibility tracking via IntersectionObserver ──
  useEffect(() => {
    if (!PROD_HOSTS.has(window.location.hostname)) return;

    const sectionTimers: Record<string, number> = {};
    const loggedSections = new Set<string>();

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const sectionId = (entry.target as HTMLElement).id;
          if (!sectionId) return;

          if (entry.isIntersecting) {
            if (!sectionTimers[sectionId]) sectionTimers[sectionId] = Date.now();
          } else if (sectionTimers[sectionId]) {
            const elapsed = Math.round((Date.now() - sectionTimers[sectionId]) / 1000);
            delete sectionTimers[sectionId];
            if (elapsed >= 2) {
              const sessionId = sessionStorage.getItem('_addis_sid');
              if (sessionId) {
                const key = `${sessionId}:${sectionId}:${pathname}`;
                if (!loggedSections.has(key)) {
                  loggedSections.add(key);
                  supabase.from('section_views').insert({
                    session_id: sessionId,
                    section_id: sectionId,
                    page_path: pathname,
                    time_spent_seconds: elapsed,
                  }).then(() => {});
                }
              }
            }
          }
        });
      },
      { threshold: 0.3 }
    );

    const observeTimer = setTimeout(() => {
      document.querySelectorAll('section[id]').forEach((el) => observer.observe(el));
    }, 2000);

    const flushSections = () => {
      const sessionId = sessionStorage.getItem('_addis_sid');
      if (!sessionId) return;
      Object.entries(sectionTimers).forEach(([sectionId, startTime]) => {
        const elapsed = Math.round((Date.now() - startTime) / 1000);
        if (elapsed >= 2) {
          const key = `${sessionId}:${sectionId}:${pathname}`;
          if (!loggedSections.has(key)) {
            loggedSections.add(key);
            fetch(`${SUPABASE_URL}/rest/v1/section_views`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'apikey': SUPABASE_ANON_KEY,
                'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
                'Prefer': 'return=minimal',
              },
              body: JSON.stringify({ session_id: sessionId, section_id: sectionId, page_path: pathname, time_spent_seconds: elapsed }),
              keepalive: true,
            }).catch(() => {});
          }
        }
      });
    };

    const onHiddenSections = () => { if (document.visibilityState === 'hidden') flushSections(); };
    document.addEventListener('visibilitychange', onHiddenSections);
    window.addEventListener('beforeunload', flushSections);

    return () => {
      clearTimeout(observeTimer);
      observer.disconnect();
      document.removeEventListener('visibilitychange', onHiddenSections);
      window.removeEventListener('beforeunload', flushSections);
    };
  }, [pathname]);
};
